import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { fmtNumber } from "@/components/Amount";   // Batch 11 · B7: 2 decimals everywhere
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import { WS_OPEN_PARAM } from "@/lib/worksheetLinks";
import React, { useEffect, useState } from "react";
import DateInput from "@/components/DateInput";
import { useSearchParams } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import FullScreenDialog from "@/components/FullScreenDialog";
import MultiFileAttachmentsPanel from "@/components/MultiFileAttachmentsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Ref from "@/components/Ref";
import { asArray } from "@/lib/asArray";
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";
import { useFormDraft, useTableDraft } from "@/lib/useDrafts";
import { saveRowFirst } from "@/lib/saveThenSend";   // STEP 11d · A9: Send saves a changed row first
import { confirmDiscard } from "@/lib/unsavedChanges";
import RpSendException from "@/components/RpSendException";   // Step 11c-3 · find 35
import RpScanResult from "@/components/RpScanResult";   // Step 11c-3 · find 33

// Step 11c-3: a coded refusal in the screen's language
const errText = (e, lang, fb) => (lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e, fb);

/**
 * Batch C · Round C3 · W16 — Related-party transactions dialog.
 * K3 Fix List — engagement id from context (not URL parsing), Add-row
 * saves, "Scan GL" is blocked with a plain EN/AR message when 211(a)
 * has no parties (no job started), and every row carries the K3 spec
 * fields: FS head/accounts, in-normal-course Y/N, YE balance +
 * confirmation link and the arm's-length evidence block.
 */
export default function RelatedPartyWorksheetDialog() {
  const { t, lang } = useI18n();
  const { currentEngId } = useEngagement();
  const [params, setParams] = useSearchParams();
  const ref = params.get(WS_OPEN_PARAM.related_party);
  const [doc, setDoc] = useState(null);
  const rpCols = useResizableColumns({ storageKey: "rp-worksheet", defaults: [180, 140, 160, 150, 120, 110, 120, 150, 110, 110, 160, 110], mins: [120, 96, 110, 100, 100, 80, 100, 100, 80, 80, 100, 90] });

  useEffect(() => {
    if (!ref || !currentEngId) { setDoc(null); return; }
    api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`, { _silent: true })
      .then((r) => setDoc(r.data)).catch((e) => { setDoc(null); toast.error(toErrMsg(e, "Load failed")); });
  }, [ref, currentEngId]);

  // Batch 28 · Follow-up 1 · F2: the TCWG date and the conclusion are a form draft (one Save / Cancel bar, one PATCH with the
  // changed fields); the party rows are a table draft (one bar; Save sends only the changed rows, each with the existing row
  // POST; a failed row keeps its error). Add row, Scan GL, send to Misstatements, attachments, mark complete and the
  // sign-offs stay immediate actions.
  const reloadDoc = async () => {
    const fresh = await api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`);
    setDoc(fresh.data);
  };
  const form = useFormDraft(doc);
  const [formSaving, setFormSaving] = useState(false);
  const td = useTableDraft({
    rows: doc ? asArray(doc.rows) : [],
    saveRow: (id, p, row) => api.post(`/engagements/${currentEngId}/related-parties/ref/${ref}/rows`, { ...row, ...p }),
    reload: reloadDoc,
  });
  useEffect(() => { form.reset(); td.cancel(); }, [ref, currentEngId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!ref) return null;

  const doClose = () => { const p = new URLSearchParams(params); p.delete(WS_OPEN_PARAM.related_party); setParams(p, { replace: true }); setDoc(null); };
  const close = async () => { if (await confirmDiscard(form.dirty || td.dirty, lang)) doClose(); };
  const saveForm = async () => {
    if (!form.dirty) return;
    setFormSaving(true);
    try { const r = await api.patch(`/engagements/${currentEngId}/related-parties/ref/${ref}`, form.patch); setDoc(r.data); form.reset(); }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); }
    finally { setFormSaving(false); }
  };
  const fv = form.value || {};
  const patch = async (body) => {
    try { const r = await api.patch(`/engagements/${currentEngId}/related-parties/ref/${ref}`, body); setDoc(r.data); }
    catch (e) { toast.error(toErrMsg(e, "Save failed")); }
  };
  const upsertRow = async (row) => {
    try {
      const r = await api.post(`/engagements/${currentEngId}/related-parties/ref/${ref}/rows`, row);
      // Reload full doc so parties_211a + can_mark_complete stay in sync.
      const fresh = await api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`);
      setDoc(fresh.data);
      return r.data;
    } catch (e) { toast.error(toErrMsg(e, "Save failed")); return null; }
  };
  const scanGl = async () => {
    if (!(doc?.parties_211a || []).length) {
      toast.info(t("rp.no211aParties"));
      return;
    }
    try { await api.post(`/engagements/${currentEngId}/related-parties/scan-gl`); toast.success(t("rp.scanRunning")); }
    catch (e) { toast.error(toErrMsg(e, "Scan failed")); }
  };
  // Step 11c-3 · find 35: the type and the exception's amount are chosen on send (RpSendException); true = sent
  const sendMs = async (row, { mtype, amount }) => {
    if (!(await saveRowFirst(td, row.id, lang))) return false;      // STEP 11d · A9 (find 30): the changed row is saved first
    try {
      // P4 fix — carry the party name so the backend description
      // becomes "RP exception — 1211-06 · TEST Party".
      await api.post(`/engagements/${currentEngId}/related-parties/ref/${ref}/send-to-misstatements`,
        { row_id: row.id, amount, mtype, party: row.related_party || "" });
      toast.success(t("rp.sentToMsOk"));
      const g = await api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`);
      setDoc(g.data);
      return true;
    } catch (e) {      // Batch 29 · M65: already in Misstatements → offer the link
      try {
        if (await offerLink(e, { engId: currentEngId, lang })) {
          const g = await api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`); setDoc(g.data); return true;
        }
      } catch (e2) { toast.error(errText(e2, lang, "Send failed")); return false; }
      toast.error(errText(e, lang, "Send failed"));
      return false;
    }
  };
  const gate = doc?.can_mark_complete || { ok: false, reasons: [] };

  return (
    <FullScreenDialog open={true} onClose={close} testid="rp-dialog"
                      title={<span data-testid="rp-title">{t("rp.title")} — <Ref>{ref}</Ref>
                        {doc?.carried_forward && (   /* Batch 10.1 · R2 — the party list carried from last year */
                          <span className="ms-2 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                                data-testid="rp-carried-forward">{t("common.carriedForward")}</span>
                        )}</span>}>
        {doc && (
          <div className="space-y-4 text-sm">
            <SignOffBar itemType="related_party" itemKey={ref} refreshKey={doc.updated_at} saveFirst={form.dirty || td.dirty}
                        onChange={() => api.get(`/engagements/${currentEngId}/related-parties/ref/${ref}`, { _silent: true }).then((r) => setDoc(r.data)).catch(() => {})} />
            <section className="rounded border p-3" data-testid="rp-parties-211a">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium">{t("rp.partiesFrom211a")}</div>
                <Button size="sm" onClick={scanGl} data-testid="rp-scan-gl">{t("rp.scanGl")}</Button>
              </div>
              {(doc.parties_211a || []).length === 0 ? (
                <div className="text-xs text-amber-800" data-testid="rp-no-parties">{t("rp.no211aParties")}</div>
              ) : (
                <ul className="list-disc ps-5 text-xs">
                  {asArray(doc.parties_211a).map((p) => <li key={p} data-testid={`rp-party-${p}`}>{p}</li>)}
                </ul>
              )}
              {/* P3 fix — always show the scan outcome, even when zero hits, plus the scan date. Step 11c-3 · find 33: current
                  period only, grouped by party, each line with its date, account and amount (RpScanResult). */}
              <RpScanResult scan={doc.scan} parties={doc.parties_211a} t={t} />
            </section>

            <section className="rounded border p-3" data-testid="rp-rows">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium">{t("rp.title")}</div>
                <Button size="sm" variant="outline" data-testid="rp-add-row"
                  onClick={() => upsertRow({ related_party: "", relationship: "", transaction_type: "",
                                              amount: 0, fs_head_accounts: "", normal_course: null,
                                              ye_balance: null, confirmation_link: "", disclosed: false,
                                              identified_by: [] })}>
                  {t("rp.addRow")}
                </Button>
              </div>
              <div className="overflow-auto">
                <ResizableTable cols={rpCols} className="w-full text-xs">
                  <thead className="border-b bg-slate-50">
                    <tr>
                      <ResizableTh index={0} className="p-2 text-start">{t("rp.relatedParty")}</ResizableTh>
                      <ResizableTh index={1} className="p-2 text-start">{t("rp.relationship")}</ResizableTh>
                      <ResizableTh index={2} className="p-2 text-start">{t("rp.fsHeadAccts")}</ResizableTh>
                      <ResizableTh index={3} className="p-2 text-start">{t("rp.transactionType")}</ResizableTh>
                      <ResizableTh index={4} className="p-2 text-end">{t("rp.amount")}</ResizableTh>
                      <ResizableTh index={5} className="p-2 text-start">{t("rp.normalCourse")}</ResizableTh>
                      <ResizableTh index={6} className="p-2 text-end">{t("rp.yeBalance")}</ResizableTh>
                      <ResizableTh index={7} className="p-2 text-start">{t("rp.confirmationLink")}</ResizableTh>
                      <ResizableTh index={8} className="p-2 text-start">{t("rp.armsLength")}</ResizableTh>
                      <ResizableTh index={9} className="p-2 text-start">{t("rp.disclosed")}</ResizableTh>
                      <ResizableTh index={10} className="p-2 text-start">{t("rp.exception")}</ResizableTh>
                      <ResizableTh index={11} className="p-2 text-start">{t("ob.action")}</ResizableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {(doc.rows || []).length === 0 && (
                      <tr><td colSpan={12} className="p-2 text-center text-muted-foreground" data-testid="rp-no-rows">—</td></tr>
                    )}
                    {asArray(doc.rows).map((r) => (
                      <RpRow key={r.id} saved={r} draft={td} sendMs={(choice) => sendMs(r, choice)} t={t} />
                    ))}
                  </tbody>
                </ResizableTable>
              </div>
              <TableSaveBar draft={td} lang={lang} testid="rp-table-save" />
            </section>

            <section className="grid gap-3 md:grid-cols-2" data-testid="rp-signoff">
              <div className="col-span-2">
                {/* Round 11 · M2 — multi-file supporting attachments. */}
                <MultiFileAttachmentsPanel engId={currentEngId}
                                             contextType="related_party"
                                             contextId={ref}
                                             testid="rp-mfa" />
              </div>
              <div>
                <Label>{t("rp.communicateTcwg")}</Label>
                <DateInput value={fv.communicated_to_tcwg_at || ""}
                  onChange={(v) => form.set("communicated_to_tcwg_at", v)}
                  data-testid="rp-tcwg-date" />
              </div>
              <div>
                <Label>{t("rp.conclusion")}</Label>
                <Textarea value={fv.conclusion || ""}
                  onChange={(e) => form.set("conclusion", e.target.value)}
                  data-testid="rp-conclusion" />
              </div>
              <div className="col-span-2">
                <SaveCancelBar dirty={form.dirty} saving={formSaving} onSave={saveForm} onCancel={form.reset} lang={lang} testid="rp-save-bar" />
              </div>
              {!gate.ok && (
                <div className="col-span-2 text-xs text-red-700" data-testid="rp-guard-reasons">
                  {gate.reasons.map((rs) => t(`rp.reasons.${rs}`) || rs).join(" · ")}
                </div>
              )}
              <div className="col-span-2 flex justify-end">
                <Button data-testid="rp-mark-complete"
                  disabled={form.dirty || td.dirty || (!doc.completed && !gate.ok)}
                  onClick={() => patch({ completed: !doc.completed })}>
                  {doc.completed ? "Unmark" : t("rp.markComplete")}
                </Button>
              </div>
            </section>
          </div>
        )}
    </FullScreenDialog>
  );
}

function RpRow({ saved, draft, sendMs, t }) {
  const [expanded, setExpanded] = useState(false);
  const r = draft.rowValue(saved);
  const upsert = (p) => Object.entries(p).forEach(([k, v]) => draft.edit(saved.id, k, v));
  return (
    <>
      <tr className={draft.rowClass(saved.id) || (r.blocks_complete ? "bg-red-50" : "")} data-testid={`rp-row-${r.id}`}
          data-changed={draft.isChanged(saved.id) ? "1" : "0"} data-failed={draft.errorOf(saved.id) ? "1" : "0"}>
        <td className="p-2">
          <RowSaveError draft={draft} id={saved.id} testid={`rp-row-error-${saved.id}`} />
          <Input className="h-7 text-xs" value={r.related_party || ""}
                  onChange={(e) => upsert({ related_party: e.target.value })}
                  data-testid={`rp-row-party-${r.id}`} />
        </td>
        <td className="p-2">
          <Input className="h-7 text-xs" value={r.relationship || ""}
                  onChange={(e) => upsert({ relationship: e.target.value })} />
        </td>
        <td className="p-2">
          <Input className="h-7 text-xs" value={r.fs_head_accounts || ""}
                  onChange={(e) => upsert({ fs_head_accounts: e.target.value })}
                  data-testid={`rp-row-fs-${r.id}`} />
        </td>
        <td className="p-2">
          <Input className="h-7 text-xs" value={r.transaction_type || ""}
                  onChange={(e) => upsert({ transaction_type: e.target.value })} />
        </td>
        <td className="p-2 text-end" dir="ltr">
          <Input className="h-7 w-24 text-xs text-end" type="number" value={r.amount ?? 0}
                  onChange={(e) => upsert({ amount: parseFloat(e.target.value || "0") })} />
        </td>
        <td className="p-2">
          <select className="h-7 text-xs" value={r.normal_course === null || r.normal_course === undefined ? "" : String(r.normal_course)}
                  onChange={(e) => upsert({ normal_course: e.target.value === "true" ? true : e.target.value === "false" ? false : null })}
                  data-testid={`rp-row-normal-${r.id}`}>
            <option value="">—</option>
            <option value="true">{t("common.yes")}</option>
            <option value="false">{t("common.no")}</option>
          </select>
        </td>
        <td className="p-2 text-end" dir="ltr">
          <Input className="h-7 w-24 text-xs text-end" type="number" value={r.ye_balance ?? ""}
                  onChange={(e) => upsert({ ye_balance: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  data-testid={`rp-row-ye-${r.id}`} />
        </td>
        <td className="p-2">
          {/* M4 fix — short placeholder (RTL was cutting off the long
              path); the full path lives in the tooltip. */}
          <Input className="h-7 text-xs" value={r.confirmation_link || ""}
                  onChange={(e) => upsert({ confirmation_link: e.target.value })}
                  placeholder={t("b3.urlRefPh")}
                  title="/confirmations/…"
                  data-testid={`rp-row-confirm-${r.id}`} />
        </td>
        <td className="p-2">
          <select className="h-7 text-xs" value={r.arms_length === null || r.arms_length === undefined ? "" : String(r.arms_length)}
                  onChange={(e) => upsert({ arms_length: e.target.value === "true" ? true : e.target.value === "false" ? false : null })}
                  data-testid={`rp-row-arms-${r.id}`}>
            <option value="">—</option>
            <option value="true">{t("common.yes")}</option>
            <option value="false">{t("common.no")}</option>
          </select>
        </td>
        <td className="p-2">
          <input type="checkbox" checked={!!r.disclosed}
                  onChange={(e) => upsert({ disclosed: e.target.checked })}
                  data-testid={`rp-row-disclosed-${r.id}`} />
        </td>
        <td className="p-2">
          {/* O3 fix — visible Exception Y/N. */}
          <select className="h-7 text-xs"
                  data-testid={`rp-row-exception-${r.id}`}
                  value={r.is_exception === null || r.is_exception === undefined ? "" : String(r.is_exception)}
                  onChange={(e) => upsert({ is_exception: e.target.value === "true" ? true : e.target.value === "false" ? false : null })}>
            <option value="">—</option>
            <option value="true">{t("common.yes")}</option>
            <option value="false">{t("common.no")}</option>
          </select>
        </td>
        <td className="p-2">
          <Button size="sm" variant="ghost" onClick={() => setExpanded((x) => !x)}
                   data-testid={`rp-row-expand-${r.id}`}>{expanded ? "▲" : "▼"}</Button>
          {/* M4 + O2/O3 fix — Send-to-misstatements only when the row
              is flagged as an exception AND has a non-zero amount.
              Idempotent — if already sent, show "Sent" + link and
              disable the button. Difference-changed sends update the
              same entry (server-side). */}
          {/* Step 11c-3 · find 35: an exception row is sent with its OWN amount (0 for a disclosure item) and the type chosen
              on send — no longer only when the row's transaction amount is non-zero */}
          {!!saved.is_exception && (
            saved.misstatement_sent ? (
              <a href={saved.misstatement_sent.link_back || "#"} target="_blank" rel="noopener"
                 className="ms-2 inline-flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800"
                 data-testid={`rp-sent-ms-${r.id}`}>
                <span>{t("rp.sentToMsOk")}</span>
                {saved.misstatement_sent.amount != null && (   /* Step 11c-3 · find 35: the amount that went to W9 */
                  <span className="tabular-nums" dir="ltr" data-testid={`rp-sent-ms-amount-${r.id}`}>{fmtNumber(saved.misstatement_sent.amount)}</span>
                )}
                <span className="underline" data-testid={`rp-sent-ms-link-${r.id}`}>{t("common.viewSent")}</span>
              </a>
            ) : (
              <RpSendException row={saved} onSend={sendMs} t={t} testid={`rp-send-ms-${r.id}`} />
            )
          )}
          {r.blocks_reason && (
            <div className="mt-1 text-[11px] text-red-700" data-testid={`rp-row-blocks-${r.id}`}>{t(`rp.reasons.${r.blocks_reason}`)}</div>
          )}
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`rp-row-details-${r.id}`}>
          <td colSpan={12} className="bg-slate-50 p-3">
            <div className="grid gap-2 md:grid-cols-2">
              {r.is_exception === true && (
                <div className="col-span-2" data-testid={`rp-exception-note-${r.id}`}>
                  <Label className="text-xs">{t("rp.exceptionNote")}</Label>
                  <Textarea value={r.exception_note || ""}
                            onChange={(e) => upsert({ exception_note: e.target.value })} />
                </div>
              )}
              <label className="text-xs">
                <input type="checkbox" checked={!!r.significant}
                        onChange={(e) => upsert({ significant: e.target.checked })} />
                &nbsp;{t("rp.significant")}
              </label>
              <label className="text-xs">
                <input type="checkbox" checked={!!r.outside_normal_course}
                        onChange={(e) => upsert({ outside_normal_course: e.target.checked })} />
                &nbsp;{t("rp.outsideNormalCourse")}
              </label>
              {r.arms_length === true && (
                <div className="col-span-2" data-testid={`rp-arms-evidence-${r.id}`}>
                  <Label className="text-xs">{t("rp.armsLengthEvidence")}</Label>
                  <Textarea value={r.arms_length_evidence || ""}
                             onChange={(e) => upsert({ arms_length_evidence: e.target.value })} />
                </div>
              )}
              {r.significant && r.outside_normal_course && (
                <div className="col-span-2" data-testid={`rp-req-evidence-${r.id}`}>
                  <div className="mb-1 text-xs font-semibold">{t("rp.requiredEvidenceTitle")}</div>
                  {[["contract_inspected", "req_contract"], ["business_rationale", "req_rationale"],
                     ["terms_consistent", "req_terms"], ["accounting_appropriate", "req_accounting"],
                     ["authorised_approved", "req_authorised"]].map(([k, tkey]) => (
                    <label key={k} className="me-3 inline-block text-xs">
                      <input type="checkbox" checked={!!(r.required_evidence || {})[k]}
                              onChange={(e) => upsert({ required_evidence: { ...(r.required_evidence || {}), [k]: e.target.checked } })}
                              data-testid={`rp-req-${k}-${r.id}`} />
                      &nbsp;{t(`rp.${tkey}`)}
                    </label>
                  ))}
                </div>
              )}
              <label className="text-xs">
                <input type="checkbox" checked={!!r.newly_identified}
                        onChange={(e) => upsert({ newly_identified: e.target.checked })} />
                &nbsp;{t("rp.newlyIdentified")}
              </label>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
